import { useState } from "react";
import { CheckIcon } from "@heroicons/react/24/solid";
import {
  ArchiveBoxIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import classNames from "../../../utils/classNames";
import useNotification from "../../notifications/useNotifications";
import { CallListStatus, type ILead } from "../../../models/Lead";
import useBookingMutations from "../../../data/useBookingMutations";
import Modal from "../../../layouts/Modal";
import { DateTime } from "luxon";

const WORKFLOW_STAGES = [
  {
    key: "awaiting_call",
    label: "Awaiting Call",
    action: "Start Call Workflow",
    icon: PhoneIcon,
    color: "amber" as const,
    callListStatus: null,
  },
  {
    key: "called_1",
    label: "1st Call",
    action: "Mark as Called",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListOne,
  },
  {
    key: "send_sms_1",
    label: "Send SMS / Email",
    action: "Mark SMS Sent",
    icon: EnvelopeIcon,
    color: "indigo" as const,
    callListStatus: CallListStatus.EmailListOne,
  },
  {
    key: "called_2",
    label: "2nd Call",
    action: "Mark as Called",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListTwo,
  },
  {
    key: "send_sms_2",
    label: "Send 2nd SMS / Email",
    action: "Mark 2nd SMS Sent",
    icon: EnvelopeIcon,
    color: "indigo" as const,
    callListStatus: CallListStatus.EmailListTwo,
  },
  {
    key: "called_3",
    label: "3rd Call",
    action: "Mark as Called",
    icon: PhoneIcon,
    color: "blue" as const,
    callListStatus: CallListStatus.CallListThree,
  },
  {
    key: "send_final",
    label: "Send Final SMS / Email",
    action: "Mark Final SMS Sent",
    icon: EnvelopeIcon,
    color: "rose" as const,
    callListStatus: CallListStatus.EmailListThree,
  },
  {
    key: "manager_approval",
    label: "Manager Approval",
    action: "Request Approval",
    icon: ShieldCheckIcon,
    color: "purple" as const,
    callListStatus: CallListStatus.ManagerApproval,
  },
  {
    key: "archived",
    label: "Archived",
    action: "Archive",
    icon: ArchiveBoxIcon,
    color: "gray" as const,
    callListStatus: CallListStatus.Archived,
  },
] as const;

const stageColorMap = {
  amber:  { currentBg: "bg-amber-100",  currentText: "text-amber-700",  currentRing: "ring-amber-400",  doneBg: "bg-amber-100",  doneText: "text-amber-600",  doneRing: "ring-amber-400",  line: "bg-amber-400"  },
  blue:   { currentBg: "bg-blue-100",   currentText: "text-blue-700",   currentRing: "ring-blue-400",   doneBg: "bg-blue-100",   doneText: "text-blue-600",   doneRing: "ring-blue-400",   line: "bg-blue-400"   },
  indigo: { currentBg: "bg-indigo-100", currentText: "text-indigo-700", currentRing: "ring-indigo-400", doneBg: "bg-indigo-100", doneText: "text-indigo-600", doneRing: "ring-indigo-400", line: "bg-indigo-400" },
  rose:   { currentBg: "bg-rose-100",   currentText: "text-rose-700",   currentRing: "ring-rose-400",   doneBg: "bg-rose-100",   doneText: "text-rose-600",   doneRing: "ring-rose-400",   line: "bg-rose-400"   },
  purple: { currentBg: "bg-purple-100", currentText: "text-purple-700", currentRing: "ring-purple-400", doneBg: "bg-purple-100", doneText: "text-purple-600", doneRing: "ring-purple-400", line: "bg-purple-400" },
  gray:   { currentBg: "bg-gray-200",   currentText: "text-gray-700",   currentRing: "ring-gray-400",   doneBg: "bg-gray-200",   doneText: "text-gray-600",   doneRing: "ring-gray-400",   line: "bg-gray-400"   },
} as const;

export default function QuoteWorkflowCard({
  lead,
  entityLabel = "Lead",
}: {
  lead: ILead;
  entityLabel?: string;
}) {
  const { updateCallStatus, addCallNote } = useBookingMutations("QuoteWorkflowCard");
  const { addNotification } = useNotification();
  const [isCallNoteModalOpen, setIsCallNoteModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<CallListStatus | null>(null);
  const [pendingCallNumber, setPendingCallNumber] = useState<number | null>(null);
  const [noteMessage, setNoteMessage] = useState("");

  const currentStageIndex = lead.callListStatus === null || lead.callListStatus === undefined
    ? 0
    : Math.max(
      0,
      WORKFLOW_STAGES.findIndex((stage) => stage.callListStatus === lead.callListStatus)
    );

  const canAdvance = currentStageIndex < WORKFLOW_STAGES.length - 1;
  const currentStage = WORKFLOW_STAGES[currentStageIndex];
  const nextStage = canAdvance ? WORKFLOW_STAGES[currentStageIndex + 1] : null;

  const getCallNumberFromStatus = (status: CallListStatus) => {
    if (status === CallListStatus.EmailListOne) return 1;
    if (status === CallListStatus.EmailListTwo) return 2;
    if (status === CallListStatus.EmailListThree) return 3;
    return null;
  };

  const updateLeadCallStatus = (status: CallListStatus, onSuccess?: () => void) => {
    updateCallStatus.mutate(
      {
        leadId: lead.id,
        status,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (err: any) => {
          addNotification({
            variant: "error",
            primaryText: err?.message ?? `Unable to update ${entityLabel.toLowerCase()} workflow`,
          });
        },
      }
    );
  };

  const closeCallNoteModal = () => {
    setIsCallNoteModalOpen(false);
    setPendingStatus(null);
    setPendingCallNumber(null);
    setNoteMessage("");
  };

  const handleStageClick = (index: number) => {
    if (index !== currentStageIndex || !nextStage || updateCallStatus.isLoading) return;
    if (nextStage.callListStatus === null) return;

    const callNumber = getCallNumberFromStatus(nextStage.callListStatus);

    if (callNumber === null) {
      updateLeadCallStatus(nextStage.callListStatus);
      return;
    }

    setPendingStatus(nextStage.callListStatus);
    setPendingCallNumber(callNumber);
    setNoteMessage("");
    setIsCallNoteModalOpen(true);
  };

  const handleSkipNote = () => {
    if (pendingStatus === null) return;

    updateLeadCallStatus(pendingStatus, closeCallNoteModal);
  };

  const handleSaveNote = () => {
    if (pendingStatus === null || !noteMessage.trim()) return;

    addCallNote.mutate(
      {
        leadId: lead.id,
        message: noteMessage.trim(),
        date: DateTime.now().toISO() ?? undefined,
        callCount: pendingCallNumber ?? undefined,
      },
      {
        onSuccess: () => {
          updateLeadCallStatus(pendingStatus, closeCallNoteModal);
        },
        onError: (err: any) => {
          addNotification({
            variant: "error",
            primaryText: err?.message ?? "Unable to save call note",
          });
        },
      }
    );
  };

  const isSaving = updateCallStatus.isLoading || addCallNote.isLoading;

  return (
    <>
      <Modal open={isCallNoteModalOpen} width={"max-w-xl"} setOpen={setIsCallNoteModalOpen}>
        <div>
          <h3 className="text-lg font-medium text-gray-900">{`Notes for call ${pendingCallNumber ?? ""}`}</h3>
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-500">Call Note</label>
            <textarea
              value={noteMessage}
              onChange={(e) => setNoteMessage(e.target.value)}
              rows={4}
              className="w-full mt-1 text-gray-900 border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 px-3 py-2 rounded-md"
              placeholder="Add optional call note"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={handleSkipNote}
              disabled={isSaving}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              No notes / Skip
            </button>
            <button
              type="button"
              onClick={handleSaveNote}
              disabled={isSaving || !noteMessage.trim()}
              className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              Save note
            </button>
          </div>
        </div>
      </Modal>
      <Card title={`${entityLabel} Workflow`} bodyClassName="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Quote CRM Workflow</h3>
            <span className="text-xs text-gray-500">
              Stage {currentStageIndex + 1} of {WORKFLOW_STAGES.length}
            </span>
          </div>

          <div className="overflow-x-auto">
            <div style={{ minWidth: "900px" }}>
              <ol className="flex items-start">
                {WORKFLOW_STAGES.map((stage, index) => {
                  const Icon = stage.icon;
                  const isDone = index < currentStageIndex;
                  const isCurrent = index === (currentStageIndex);
                  const isClickable = isCurrent && canAdvance && !updateCallStatus.isLoading;
                  const sc = stageColorMap[stage.color];

                  return (
                    <li key={stage.key} className="relative flex flex-1 flex-col items-center text-center">
                      <button
                        type="button"
                        onClick={() => handleStageClick(index)}
                        disabled={!isClickable}
                        title={isCurrent ? stage.action : ""}
                        className={classNames(
                          "group relative z-10 flex flex-col items-center rounded-lg px-2 py-2 transition-all",
                          isClickable ? "cursor-pointer hover:bg-gray-50" : "cursor-default opacity-60"
                        )}
                      >
                        <div
                          className={classNames(
                            "mb-1.5 flex h-9 w-9 items-center justify-center rounded-full ring-2 transition-all",
                            isDone
                              ? `${sc.doneBg} ${sc.doneText} ${sc.doneRing}`
                              : isCurrent
                                ? `${sc.currentBg} ${sc.currentText} ${sc.currentRing} shadow-md hover:shadow-lg`
                                : "bg-gray-100 text-gray-400 ring-gray-200"
                          )}
                        >
                          {isDone ? (
                            <CheckIcon className="h-4.5 w-4.5" />
                          ) : (
                            <Icon className="h-4.5 w-4.5" />
                          )}
                        </div>

                        <p
                          className={classNames(
                            "text-[11px] font-medium leading-tight max-w-[90px]",
                            isDone
                              ? sc.doneText
                              : isCurrent
                                ? `${sc.currentText} font-semibold`
                                : "text-gray-400"
                          )}
                        >
                          {stage.label}
                        </p>

                        {isDone && (
                          <span className={classNames("mt-0.5 text-[9px] font-medium", sc.doneText)}>
                            Done
                          </span>
                        )}

                        {isCurrent && (
                          <span className={classNames("mt-0.5 text-[9px] font-medium", sc.currentText)}>
                            {updateCallStatus.isLoading ? "Saving..." : stage.action}
                          </span>
                        )}
                      </button>

                      {index < WORKFLOW_STAGES.length - 1 && (
                        <div className="absolute left-1/2 top-[18px] h-0.5 w-full px-6">
                          <div
                            className={classNames(
                              "h-full w-full rounded-full transition-colors",
                              isDone ? sc.line : "bg-gray-200"
                            )}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {nextStage
              ? `Click "${currentStage.label}" to move this ${entityLabel.toLowerCase()} to "${nextStage.label}".`
              : `${entityLabel} is at the final workflow stage.`}
          </p>
        </div>
      </Card>
    </>
  );
}
